/**
 * Logique pure des composants communs (tri, pagination, échelles des graphiques, tons des
 * statuts) : sans React, testée à part (apps/web/tests/unit/design-logic.test.ts).
 */

export type SortDirection = 'asc' | 'desc';
export type SortValue = string | number | boolean | Date | null | undefined;

const collator = new Intl.Collator('fr', { sensitivity: 'base', numeric: true });

/** Comparaison « à la française » : sans accents ni casse, nombres naturels, vides à la fin. */
export function compareValues(a: SortValue, b: SortValue): number {
  const empty = (v: SortValue): boolean => v === null || v === undefined || v === '';
  if (empty(a) && empty(b)) return 0;
  if (empty(a)) return 1;
  if (empty(b)) return -1;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return collator.compare(String(a), String(b));
}

/** Tri stable d'une copie ; les valeurs vides restent en fin de liste dans les deux sens. */
export function sortRows<T>(
  rows: readonly T[],
  value: (row: T) => SortValue,
  direction: SortDirection,
): T[] {
  const indexed = rows.map((row, index) => ({ row, index, v: value(row) }));
  indexed.sort((x, y) => {
    const emptyX = x.v === null || x.v === undefined || x.v === '';
    const emptyY = y.v === null || y.v === undefined || y.v === '';
    if (emptyX !== emptyY) return emptyX ? 1 : -1;
    const c = compareValues(x.v, y.v);
    return (direction === 'asc' ? c : -c) || x.index - y.index;
  });
  return indexed.map((i) => i.row);
}

export interface PageSlice<T> {
  items: T[];
  page: number;
  pageCount: number;
  from: number;
  to: number;
  total: number;
}

/** Découpe en pages (numérotées à partir de 1) ; une page hors bornes est ramenée dedans. */
export function paginate<T>(rows: readonly T[], page: number, pageSize: number): PageSlice<T> {
  const total = rows.length;
  const size = Math.max(1, Math.floor(pageSize));
  const pageCount = Math.max(1, Math.ceil(total / size));
  const current = Math.min(Math.max(1, Math.floor(page)), pageCount);
  const start = (current - 1) * size;
  const items = rows.slice(start, start + size);
  return {
    items,
    page: current,
    pageCount,
    from: total === 0 ? 0 : start + 1,
    to: start + items.length,
    total,
  };
}

/** Recherche sans accents ni casse dans les textes d'une ligne. */
export function matchesQuery(
  texts: readonly (string | null | undefined)[],
  query: string,
): boolean {
  const fold = (v: string): string => v.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const q = fold(query.trim());
  if (q === '') return true;
  return texts.some((t) => t != null && fold(t).includes(q));
}

/** Maximum « rond » d'un axe (1, 2, 2,5, 5 × 10ⁿ) au-dessus de la plus grande valeur. */
export function niceMax(max: number): number {
  if (!Number.isFinite(max) || max <= 0) return 1;
  const exp = Math.floor(Math.log10(max));
  const base = 10 ** exp;
  for (const step of [1, 2, 2.5, 5, 10]) if (step * base >= max) return step * base;
  return 10 * base;
}

/**
 * Graduations régulières de 0 à un maximum rond couvrant `max`, au pas « rond » (1, 2, 5 ×
 * 10ⁿ, entier dès que possible) donnant au plus `count` intervalles ; la dernière graduation
 * est le haut de l'axe.
 */
export function ticks(max: number, count = 4): number[] {
  const value = Number.isFinite(max) && max > 0 ? max : 1;
  const raw = value / count;
  const exp = Math.floor(Math.log10(raw));
  const base = 10 ** exp;
  const step = ([1, 2, 5, 10].find((s) => s * base >= raw) ?? 10) * base;
  const n = Math.max(1, Math.ceil(value / step - 1e-9));
  return Array.from({ length: n + 1 }, (_, i) => Math.round(step * i * 1000) / 1000);
}

/** Parts en pourcentage arrondies dont la somme fait exactement 100 (plus forts restes). */
export function percentages(values: readonly number[]): number[] {
  const total = values.reduce((s, v) => s + Math.max(0, v), 0);
  if (total === 0) return values.map(() => 0);
  const raw = values.map((v) => (Math.max(0, v) / total) * 100);
  const floored = raw.map(Math.floor);
  let rest = 100 - floored.reduce((s, v) => s + v, 0);
  const order = raw
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (const { i } of order) {
    if (rest <= 0) break;
    floored[i] = (floored[i] ?? 0) + 1;
    rest -= 1;
  }
  return floored;
}

/** Arcs d'un anneau (angles en radians, départ à midi, sens horaire). */
export function donutArcs(values: readonly number[]): { start: number; end: number }[] {
  const total = values.reduce((s, v) => s + Math.max(0, v), 0);
  let angle = 0;
  return values.map((v) => {
    const sweep = total === 0 ? 0 : (Math.max(0, v) / total) * Math.PI * 2;
    const arc = { start: angle, end: angle + sweep };
    angle += sweep;
    return arc;
  });
}

export type Tone = 'neutral' | 'info' | 'ok' | 'warning' | 'critical';

/**
 * Ton d'un statut métier, commun à tous les modules : même couleur pour un même sens
 * (terminé = vert, en attente = orange, en retard ou annulé = rouge, en cours = bleu).
 */
export function statusTone(status: string | null | undefined): Tone {
  if (status == null) return 'neutral';
  const s = status.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  if (/(inactif|archiv)/.test(s)) return 'neutral';
  if (
    /(annul|refus|erreur|echec|retard|rouge|urgent|critique|perime|depass|bloqu|abandon|non_inscri)/.test(
      s,
    )
  )
    return 'critical';
  if (
    /(non[ _]publi|attente|propos|brouillon|surveill|orange|bientot|a_renseigner|a_valider|nouveau|recue)/.test(
      s,
    )
  )
    return 'warning';
  if (
    /(publi|confirm|resolu|valid|termin|integr|^ok$|^vert$|actif|active|pret|livr|ferme|accept)/.test(
      s,
    )
  )
    return 'ok';
  if (/(cours|ouvert|prevu|session|planifi|inscrit)/.test(s)) return 'info';
  return 'neutral';
}

/* ------------------------------------------------------------------ */
/* Réglages, raccourcis clavier, onglets (docs/ux/composants.md)       */
/* ------------------------------------------------------------------ */

const definedKeys = (o: object): string[] =>
  Object.keys(o).filter((k) => (o as Record<string, unknown>)[k] !== undefined);

/** Comparaison profonde de valeurs de formulaire (JSON) : sert à l'état « modifié ». */
export function sameValues(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = definedKeys(a);
  const kb = definedKeys(b);
  if (ka.length !== kb.length) return false;
  return ka.every((k) =>
    sameValues((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]),
  );
}

/** Champs modifiés entre la valeur enregistrée et la saisie (clés de premier niveau). */
export function changedKeys<T extends object>(saved: T, current: T): (keyof T)[] {
  const keys = new Set([...Object.keys(saved), ...Object.keys(current)] as (keyof T)[]);
  return [...keys].filter((k) => !sameValues(saved[k], current[k]));
}

export interface Hotkey {
  key: string;
  /** Ctrl sous Windows / Linux, Cmd sous macOS. */
  mod: boolean;
  shift: boolean;
  alt: boolean;
}

/** Lit un raccourci écrit `mod+s`, `shift+n`, `/`, `?`, `escape`. */
export function parseHotkey(spec: string): Hotkey {
  const parts = spec.toLowerCase().split('+');
  const last = parts.pop() ?? '';
  return {
    key: last === '' ? '+' : last,
    mod: parts.includes('mod') || parts.includes('ctrl') || parts.includes('cmd'),
    shift: parts.includes('shift'),
    alt: parts.includes('alt'),
  };
}

export interface KeyLike {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}

/** L'événement clavier correspond-il au raccourci ? (`?`, `/`… ignorent Maj). */
export function matchesHotkey(spec: string | Hotkey, event: KeyLike): boolean {
  const h = typeof spec === 'string' ? parseHotkey(spec) : spec;
  const mod = event.ctrlKey || event.metaKey;
  if (h.mod !== mod || h.alt !== event.altKey) return false;
  const symbol = h.key.length === 1 && !/[a-z0-9]/.test(h.key);
  if (!symbol && h.shift !== event.shiftKey) return false;
  return event.key.toLowerCase() === h.key;
}

/** Libellé lisible d'un raccourci (`Ctrl+S`, `⌘S` sur Mac). */
export function hotkeyLabel(spec: string, mac = false): string {
  const h = parseHotkey(spec);
  const key =
    h.key.length === 1 ? h.key.toUpperCase() : h.key.charAt(0).toUpperCase() + h.key.slice(1);
  const parts = [h.mod ? (mac ? '⌘' : 'Ctrl') : '', h.shift ? 'Maj' : '', h.alt ? 'Alt' : '', key];
  return parts.filter(Boolean).join(mac ? '' : '+');
}

/** Onglet retenu : celui de l'adresse s'il existe, sinon le premier. */
export function pickTab(keys: readonly string[], requested: string | null | undefined): string {
  return requested !== null && requested !== undefined && keys.includes(requested)
    ? requested
    : (keys[0] ?? '');
}

/** Étape du chemin d'arrivée sur une fiche liée (fil d'Ariane des interconnexions). */
export interface TrailStep {
  label: string;
  to: string;
}

/** Nombre d'étapes gardées dans le chemin d'arrivée. */
export const TRAIL_MAX = 3;

/** Chemin d'arrivée porté par l'état de navigation (`{ trail }`), vide sinon. */
export function readTrail(state: unknown): TrailStep[] {
  if (typeof state !== 'object' || state === null || !('trail' in state)) return [];
  const trail: unknown = state.trail;
  if (!Array.isArray(trail)) return [];
  return trail
    .filter(
      (s): s is TrailStep =>
        typeof s === 'object' &&
        s !== null &&
        typeof (s as TrailStep).label === 'string' &&
        typeof (s as TrailStep).to === 'string',
    )
    .slice(-TRAIL_MAX);
}

/** Chemin prolongé de la page quittée (sans doublon, `TRAIL_MAX` étapes au plus). */
export function extendTrail(current: readonly TrailStep[], here: TrailStep): TrailStep[] {
  return [...current.filter((s) => s.to !== here.to), here].slice(-TRAIL_MAX);
}
